import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Paper,
  Modal,
  Button,
  Select,
  MenuItem,
  Grid,
  Card,
  Tooltip,
} from "@mui/material";
import { eachDayOfInterval, startOfMonth, endOfMonth } from "date-fns";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const convertToUTCDate = (isoString) => {
  const date = new Date(isoString);
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
};

const CalendarView = ({
  nextReservations,
  modalOpen,
  setModalOpen,
  selectedReservation,
  setSelectedReservation,
  selectedMonth,
  setSelectedMonth,
  expandedReservation,
  setExpandedReservation,
  setReservationToShow,
  setView,
}) => {
  const { user } = useAuth();
  const rooms = user?.rooms || [];
  const navigate = useNavigate();

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(selectedMonth),
    end: endOfMonth(selectedMonth),
  });

  const roomMap = {};
  const groupedReservations = {};

  rooms.forEach((room) => {
    const isPrivate = room.type === "private";
    const uniqueKey = isPrivate
      ? `${room.roomId}_${room.unitId}`
      : `${room.roomId}`;
    roomMap[uniqueKey] = room;
    if (!groupedReservations[uniqueKey]) groupedReservations[uniqueKey] = [];

    nextReservations.forEach((res) => {
      if (isPrivate) {
        if (res.roomId === room.roomId && res.unitId === room.unitId) {
          groupedReservations[uniqueKey].push(res);
        }
      } else {
        if (res.roomId === room.roomId) {
          groupedReservations[uniqueKey].push(res);
        }
      }
    });
  });

  const handleOpenReservation = (reservations, day) => {
    setSelectedReservation({ reservations, day });
    setModalOpen(true);
  };

  const handleExpandReservation = (reservation) => {
    setReservationToShow(reservation.reservationId);
    setView("reserva_unica");
  };
  const Legend = ({ color, label }) => (
    <Box display="flex" alignItems="center" gap={1}>
      <Box width={14} height={14} bgcolor={color} border="1px solid #999" />
      <Typography variant="caption">{label}</Typography>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ mt: 1, mb: 1, ml: 1 }}>
        <Select
          value={selectedMonth.toISOString().slice(0, 7)}
          onChange={(e) => {
            const [year, month] = e.target.value.split("-");
            setSelectedMonth(new Date(parseInt(year), parseInt(month) - 1, 1));
          }}
          sx={{
            minWidth: 220,
            height: 40,
            backgroundColor: "#fff",
            color: "#565254",
            borderRadius: "8px",
            px: 2,
            fontWeight: 500,
            fontSize: "14px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            border: "1px solid #ccc",
            "&:hover": { borderColor: "#903AF2" },
            "&.Mui-focused": { borderColor: "#903AF2" },
            "& .MuiSelect-icon": { color: "#565254" },
          }}
        >
          {[...Array(12).keys()].map((i) => {
            const month = new Date(new Date().getFullYear(), i, 1);
            return (
              <MenuItem
                key={i}
                value={month.toISOString().slice(0, 7)}
                sx={{
                  color: "#565254",
                  fontSize: "14px",
                  "&:hover": { backgroundColor: "#EAF7CF" },
                }}
              >
                {month.toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                })}
              </MenuItem>
            );
          })}
        </Select>
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <Box
          sx={{
            backgroundColor: "#fff",
            display: "inline-block",
            minWidth: `${daysInMonth.length * 52 + 120}px`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#fff",
              height: "80vh",
              justifyContent: "space-between",
            }}
          >
            <Box display="flex" gap={2} p={1}>
              <Legend color="#C8E6C9" label="Check-in" />
              <Legend color="#FFE0B2" label="Check-out" />
              <Legend color="#FFF9C4" label="Rotación" />
              <Legend color="#D1C4E9" label="Estadía" />
            </Box>

            {/* Encabezado */}
            <Box
              sx={{
                display: "flex",
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  width: "120px",
                  minWidth: "120px",
                  textAlign: "center",
                  fontWeight: "bold",
                  color: "#565254",
                  borderRight: "1px solid #ccc",
                  backgroundColor: "#fff",
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                }}
              >
                Fecha x Habitación
              </Box>
              {daysInMonth.map((day) => {
                const isToday =
                  new Date().toDateString() === day.toDateString();

                return (
                  <Box
                    key={day.toISOString()}
                    sx={{
                      minWidth: "52px",
                      maxWidth: "51px",
                      height: "41px",
                      textAlign: "center",
                      fontWeight: "bold",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: isToday ? "#F06654" : "#fff", // 💚 color para hoy
                      borderRadius: isToday ? "4px" : 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "10px",
                        color: isToday ? "#fff" : "#565254", // 💚 color para hoy
                      }}
                    >
                      {day
                        .toLocaleDateString("es-ES", { weekday: "short" })
                        .charAt(0)
                        .toUpperCase()}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12px",
                        color: isToday ? "#fff" : "#565254", // 💚 color para hoy
                      }}
                    >
                      {day.getDate()}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Filas */}
            {Object.entries(groupedReservations).map(([key, reservations]) => {
              const room = roomMap[key];
              return (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#fff",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <Box
                    sx={{
                      minWidth: "130px",
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                      backgroundColor: "#f5f5f5",
                      borderRight: "1px solid #ddd",
                      py: 1,
                    }}
                  >
                    {(room?.name &&
                      room.name.charAt(0).toUpperCase() + room.name.slice(1)) ||
                      "Sin nombre"}
                  </Box>

                  {daysInMonth.map((day) => {
                    const utcDay = new Date(
                      Date.UTC(day.getFullYear(), day.getMonth(), day.getDate())
                    );

                    const dayReservations = reservations.filter((res) => {
                      const checkIn = convertToUTCDate(res.startDate);
                      const checkOut = convertToUTCDate(res.endDate);
                      return utcDay >= checkIn && utcDay <= checkOut;
                    });

                    const isCheckIn = dayReservations.some(
                      (res) =>
                        convertToUTCDate(res.startDate).getTime() ===
                        utcDay.getTime()
                    );
                    const isCheckOut = dayReservations.some(
                      (res) =>
                        convertToUTCDate(res.endDate).getTime() ===
                        utcDay.getTime()
                    );
                    const isRotationDay =
                      isCheckIn && isCheckOut && dayReservations.length > 1;

                    let backgroundColor = "#ffffff";
                    let border = "1px solid #ddd";
                    let statusText = "";

                    if (isRotationDay) {
                      backgroundColor = "#FFF9C4"; // Amarillo claro
                      border = "1px solid #FBC02D";
                      statusText = "↔";
                    } else if (isCheckIn) {
                      backgroundColor = "#C8E6C9"; // Verde claro
                      border = "1px solid #81C784";
                      statusText = "IN";
                    } else if (isCheckOut) {
                      backgroundColor = "#FFE0B2"; // Naranja claro
                      border = "1px solid #FFB74D";
                      statusText = "OUT";
                    } else if (dayReservations.length > 0) {
                      backgroundColor = "#D1C4E9"; // Lavanda claro
                      border = "1px solid #B39DDB";
                      statusText =
                        dayReservations[0]?.rooms?.[0]?.room?.substring(
                          0,
                          10
                        ) ||
                        dayReservations[0]?.fullName?.substring(0, 10) ||
                        "";
                    }

                    return (
                      <Tooltip
                        key={utcDay.toISOString()}
                        title={
                          dayReservations.length > 0
                            ? dayReservations
                                .map(
                                  (res) =>
                                    `${res.fullName} (${res.startDate} → ${res.endDate})`
                                )
                                .join("\n")
                            : ""
                        }
                        arrow
                      >
                        <Paper
                          sx={{
                            width: "52px",
                            height: "40px",
                            backgroundColor,
                            border,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            boxShadow: "none",
                            cursor:
                              dayReservations.length > 0
                                ? "pointer"
                                : "default",
                            transition: "transform 0.2s",
                            "&:hover": {
                              transform:
                                dayReservations.length > 0
                                  ? "scale(1.08)"
                                  : "none",
                              zIndex: 3,
                            },
                          }}
                          onClick={() =>
                            dayReservations.length > 0 &&
                            handleOpenReservation(dayReservations, utcDay)
                          }
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#000",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              textAlign: "center",
                              lineHeight: 1.1,
                            }}
                          >
                            {statusText}
                          </Typography>
                        </Paper>
                      </Tooltip>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      {selectedReservation && (
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            overflow: "auto",
            p: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "1100px",
              bgcolor: "#ECECEC",
              borderRadius: 3,
              p: 3,
              boxShadow: 24,
            }}
          >
            <Typography
              variant="h4"
              fontWeight="bold"
              color="#903AF2"
              textAlign="center"
              mb={3}
            >
              Habitación:{" "}
              {selectedReservation?.reservations?.[0]?.rooms?.[0]?.room ||
                "No Disponible"}
            </Typography>

            <Grid container spacing={3}>
              {selectedReservation.reservations.map((reservation, index) => {
                const isExpanded = expandedReservation === reservation;
                return (
                  <Grid item xs={12} key={index}>
                    <Card
                      sx={{
                        backgroundColor: "#fff",
                        borderRadius: 2,
                        p: 2,
                        boxShadow: isExpanded ? 6 : 1,
                      }}
                      onClick={() => handleExpandReservation(reservation)}
                    >
                      <Typography variant="subtitle1">
                        <strong>Huésped:</strong> {reservation.fullName}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Ingreso:</strong>{" "}
                        {reservation.startDate.slice(0, 10)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Salida:</strong>{" "}
                        {reservation.endDate.slice(0, 10)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Estado:</strong>{" "}
                        {reservation.estado || "Pendiente"}
                      </Typography>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <Button
              fullWidth
              variant="contained"
              sx={{
                marginTop: "30px",
                padding: "12px",
                fontWeight: "bold",
                backgroundColor: "#903AF2",
                "&:hover": { backgroundColor: "#6e2bc1" },
              }}
              onClick={() => setModalOpen(false)}
            >
              Cerrar
            </Button>
          </Box>
        </Modal>
      )}
    </Box>
  );
};

export default CalendarView;
